import type { BookSide } from '@sharpr/types';
import { PMF_MASS_TOLERANCE, solveTilt, type Pmf } from './pmf';
import type { PropLine } from './prop';

export function americanToDecimal(odds: number): number {
  return odds > 0 ? odds / 100 + 1 : 100 / -odds + 1;
}

// A decimal of 1 or less pays nothing back: it has no American odds.
export function decimalToAmerican(decimal: number): number {
  if (!(decimal > 1 && Number.isFinite(decimal))) throw new RangeError(`decimal odds must be finite and above 1 (got ${decimal})`);
  return Math.round(decimal >= 2 ? (decimal - 1) * 100 : -100 / (decimal - 1));
}

export function impliedProbability(odds: number): number {
  return 1 / americanToDecimal(odds);
}

// A certain or impossible side has no American odds.
export function probabilityToAmerican(p: number): number {
  if (!(p > 0 && p < 1)) throw new RangeError(`probability must be inside (0, 1) (got ${p})`);
  return decimalToAmerican(1 / p);
}

export function expectedValue(pWin: number, decimal: number, pPush = 0): number {
  return pWin * decimal - (1 - pPush);
}

const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));

export function lineProbabilities(pmf: Pmf, line: number): { over: number; under: number; push: number } {
  if (!Number.isFinite(line) || line < 0 || !Number.isInteger(line * 2)) {
    throw new RangeError(`line must be on the half-point grid (got ${line})`);
  }
  let under = 0;
  let over = 0;
  for (let k = 0; k < pmf.length; k++) {
    if (k < line) under += pmf[k];
    else if (k > line) over += pmf[k];
  }
  const push = Number.isInteger(line) && line < pmf.length ? pmf[line] : 0;
  // A PMF short of mass by the tolerance must still read as a certain under past its support (mass is
  // only ever lost from the truncated upper tail); 2× absorbs the summation's own rounding.
  if (over === 0 && push === 0 && under >= 1 - 2 * PMF_MASS_TOLERANCE) return { over: 0, under: 1, push: 0 };
  return { over: clamp01(over), under: clamp01(under), push: clamp01(push) };
}

// A push refunds the stake, so a side's fair probability is its share of the outcomes that settle. Null when none
// settle: all the mass on a whole line.
export function fairProbability(pmf: Pmf, line: number, side: BookSide): number | null {
  const { over, under } = lineProbabilities(pmf, line);
  if (over + under === 0) return null;
  return (side === 'over' ? over : under) / (over + under);
}

export interface SideEv { ev: number; pWin: number; pPush: number }

// Undefined when the prop line has no odds on that side.
export function sideEv(pmf: Pmf, propLine: Pick<PropLine, 'line' | 'over' | 'under'>, side: BookSide): SideEv | undefined {
  const odds = propLine[side];
  if (odds === undefined) return undefined;
  const p = lineProbabilities(pmf, propLine.line);
  return { ev: expectedValue(p[side], americanToDecimal(odds), p.push), pWin: p[side], pPush: p.push };
}

// Floored so a near-zero-hold book can't own the blend. Weight is 1/hold, not 1/hold²: books agree
// to ~1pp regardless of hold, so squaring collapses the blend onto one book.
const HOLD_FLOOR = 0.01;
// Clean pairs run to 29.8% hold; sentinel pairs (-20000/-20000) start at 30.8%.
const MAX_TWO_WAY_HOLD = 0.30;

// The lower median.
function weightedMedian(values: readonly number[], weights: readonly number[]): number {
  const idx = values.map((_, i) => i).sort((a, b) => values[a] - values[b]);
  const total = weights.reduce((s, w) => s + w, 0);
  let cum = 0;
  for (const i of idx) { cum += weights[i]; if (cum >= total / 2) return values[i]; }
  return values[idx[idx.length - 1]];
}

// What the market rules read of a book's line.
export type BookQuote = Pick<PropLine, 'book' | 'line' | 'over' | 'under' | 'removed'>;
export type TwoWayPropLine = BookQuote & { over: number; under: number };

const hold = (propLine: TwoWayPropLine): number => impliedProbability(propLine.over) + impliedProbability(propLine.under) - 1;

// DFS payouts are fixed, not a de-viggable market.
export function isTwoWay<P extends BookQuote>(propLine: P, dfsBooks: ReadonlySet<string>): propLine is P & TwoWayPropLine {
  return !propLine.removed && !dfsBooks.has(propLine.book) && propLine.over !== undefined && propLine.under !== undefined
    && hold(propLine as TwoWayPropLine) < MAX_TWO_WAY_HOLD;
}

export function holdWeight(propLine: TwoWayPropLine): number {
  return 1 / Math.max(hold(propLine), HOLD_FLOOR);
}

// Additive, identical to Shin (1993) for two outcomes; multiplicative overstates longshots.
export function devig(propLine: TwoWayPropLine, side: BookSide): number {
  return impliedProbability(propLine[side]) - hold(propLine) / 2;
}

export interface Consensus { impliedProbability: number; bookCt: number }

// Deliberately vigged: odds you could be offered, not a fair line.
export function consensus(propLines: readonly PropLine[], line: number, side: BookSide, dfsBooks: ReadonlySet<string>): Consensus | null {
  const books = propLines.filter((p): p is PropLine & TwoWayPropLine => p.line === line && isTwoWay(p, dfsBooks));
  if (books.length === 0) return null;
  const implied = weightedMedian(books.map(p => impliedProbability(p[side])), books.map(holdWeight));
  return { impliedProbability: implied, bookCt: books.length };
}

// Tilts the model PMF until it agrees with the book at the book's line, then reads ours. Null when
// the model has no mass on one side of the book's line: no tilt can agree.
export function repriceToLine(modelPmf: Pmf, bookLine: number, overAtBook: number, placedLine: number): number | null {
  const modelOver = fairProbability(modelPmf, bookLine, 'over');
  if (modelOver === null || !(modelOver > 0 && modelOver < 1)) return null;
  // A tilt keeps the support, and the support's ends sit on either side of the book's line: some outcome settles.
  const agreed = solveTilt(modelPmf, tilted => fairProbability(tilted, bookLine, 'over')!, overAtBook);
  return fairProbability(agreed, placedLine, 'over');
}

export interface ClvInput {
  propLines: readonly PropLine[];
  side: BookSide;
  placedOdds: number;
  placedLine: number;
  modelPmf?: Pmf;
  dfsBooks: ReadonlySet<string>;
}

// clv: the close's fair probability over our break-even, minus 1. lineMove: points between the
// close's median line and ours. Both positive when the close moved our way.
export interface ClvOutput {
  clv: number;
  bookCt: number;
  lineMove: number;
}

// One two-way book's fair probability of the over at `line`: its de-vigged over where it quotes that line, else
// repriced there through the model. Null when it can't be (no model, or no tilt agrees).
export function fairOverAt(book: TwoWayPropLine, line: number, modelPmf?: Pmf): number | null {
  const fairOver = devig(book, 'over');
  if (book.line === line) return fairOver;
  return modelPmf ? repriceToLine(modelPmf, book.line, fairOver, line) : null;
}

// over: the market's fair probability of the over at `line`, the 1/hold-weighted mean of the two-way books' fair overs
// there. line: their central line, the weighted median (lines are discrete settlement points, so not the mean).
export interface FairConsensus { over: number; line: number; bookCt: number }

// One book's part in the consensus: its fair over at `line`, its own line and its weight. Null for a book that has
// none (not two-way, or no fair over there).
export interface FairEstimate { over: number; line: number; weight: number }

export function fairEstimate(propLine: BookQuote, line: number, modelPmf: Pmf | undefined, dfsBooks: ReadonlySet<string>): FairEstimate | null {
  if (!isTwoWay(propLine, dfsBooks)) return null;
  const over = fairOverAt(propLine, line, modelPmf);
  return over == null ? null : { over, line: propLine.line, weight: holdWeight(propLine) };
}

export function fairConsensus(propLines: readonly BookQuote[], line: number, modelPmf: Pmf | undefined, dfsBooks: ReadonlySet<string>): FairConsensus | null {
  return blendFair(propLines.flatMap(p => fairEstimate(p, line, modelPmf, dfsBooks) ?? []));
}

// The consensus of the books' estimates (fairEstimate), for a caller that keeps them.
export function blendFair(estimates: readonly FairEstimate[]): FairConsensus | null {
  if (estimates.length === 0) return null;
  const weights = estimates.map(e => e.weight);
  return {
    over: estimates.reduce((sum, e) => sum + e.over * e.weight, 0) / weights.reduce((sum, w) => sum + w, 0),
    line: weightedMedian(estimates.map(e => e.line), weights),
    bookCt: estimates.length,
  };
}

export function computeClv({ propLines, side, placedOdds, placedLine, modelPmf, dfsBooks }: ClvInput): ClvOutput | null {
  const close = fairConsensus(propLines, placedLine, modelPmf, dfsBooks);
  if (!close) return null;
  const fair = side === 'over' ? close.over : 1 - close.over;
  const lineDelta = close.line - placedLine;
  return {
    clv: fair / impliedProbability(placedOdds) - 1,
    bookCt: close.bookCt,
    lineMove: side === 'over' ? lineDelta : -lineDelta,
  };
}
